/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Speaker Dialogue Studio & Timeline DTOs (Task 7.4.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';
import { AudioMixerProjectSchema } from './mixer.dto';

/**
 * AI Voice Provider Types for Multi-Speaker Dialogue Characters.
 */
export const SpeakerVoiceProviderSchema = z
  .enum(['openai', 'elevenlabs', 'google', 'azure', 'clone'])
  .openapi({
    description: 'TTS Provider powering character voice generation',
    example: 'elevenlabs',
  });

export type SpeakerVoiceProvider = z.infer<typeof SpeakerVoiceProviderSchema>;

/**
 * Character / Speaker profile definition in a dialogue script.
 */
export const ScriptSpeakerSchema = z
  .object({
    id: z.string().min(1).openapi({ description: 'Speaker unique identifier', example: 'spk_host_alex' }),
    name: z.string().min(1).max(100).openapi({ description: 'Speaker display name', example: 'Alex (Host)' }),
    avatarUrl: z.string().url().optional().openapi({ description: 'Speaker avatar image URL', example: 'https://media.sophiaplatform.com/avatars/alex.png' }),
    voiceProvider: SpeakerVoiceProviderSchema.default('openai'),
    voiceId: z.string().min(1).openapi({ description: 'Voice identifier in provider catalog', example: 'alloy' }),
    speed: z.number().min(0.25).max(4.0).default(1.0).openapi({ description: 'Speech rate multiplier (0.25 - 4.0)', example: 1.0 }),
    pitch: z.number().min(-20).max(20).default(0).openapi({ description: 'Speech pitch shift (-20 to 20)', example: 0 }),
    color: z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/).default('#6366F1').openapi({ description: 'Timeline track badge hex color', example: '#6366F1' }),
  })
  .openapi('ScriptSpeaker');

export type ScriptSpeakerDto = z.infer<typeof ScriptSpeakerSchema>;

/**
 * Individual speech block synthesis status.
 */
export const ScriptBlockStatusSchema = z
  .enum(['idle', 'synthesizing', 'ready', 'error'])
  .openapi({
    description: 'Synthesis lifecycle status for this speech block',
    example: 'ready',
  });

export type ScriptBlockStatus = z.infer<typeof ScriptBlockStatusSchema>;

/**
 * Single dialogue sentence / speech block on the multi-speaker timeline.
 */
export const ScriptBlockSchema = z
  .object({
    id: z.string().min(1).openapi({ description: 'Block unique identifier', example: 'blk_01HXYZ123' }),
    speakerId: z.string().min(1).openapi({ description: 'Speaker assigned to this dialogue block', example: 'spk_host_alex' }),
    speakerName: z.string().min(1).openapi({ description: 'Speaker name snapshot', example: 'Alex (Host)' }),
    text: z.string().min(1).max(5000).openapi({ description: 'Dialogue line text content', example: 'Welcome back to the Future of AI podcast!' }),
    speed: z.number().min(0.25).max(4.0).optional().openapi({ description: 'Optional speed override for this sentence', example: 1.05 }),
    pitch: z.number().min(-20).max(20).optional().openapi({ description: 'Optional pitch override for this sentence', example: 1 }),
    pauseAfter: z.number().min(0).max(30).default(0.5).openapi({ description: 'Silence pause after this sentence in seconds', example: 0.5 }),
    audioUrl: z.string().url().optional().openapi({ description: 'Synthesized audio URL on zero-egress CDN', example: 'https://media.sophiaplatform.com/audio/ws_01/dialogue/blk_1.mp3' }),
    r2Key: z.string().optional().openapi({ description: 'Cloudflare R2 destination key', example: 'audio/ws_01/dialogue/blk_1.mp3' }),
    duration: z.number().min(0).default(0).openapi({ description: 'Exact audio duration in seconds', example: 3.42 }),
    status: ScriptBlockStatusSchema.default('idle'),
    errorMessage: z.string().optional().openapi({ description: 'Error message if synthesis failed' }),
    startTime: z.number().min(0).default(0).openapi({ description: 'Calculated timeline start timestamp in seconds', example: 0 }),
    endTime: z.number().min(0).default(0).openapi({ description: 'Calculated timeline end timestamp in seconds', example: 3.42 }),
  })
  .openapi('ScriptBlock');

export type ScriptBlockDto = z.infer<typeof ScriptBlockSchema>;

/**
 * Full Multi-Speaker Dialogue Script Schema.
 */
export const DialogueScriptSchema = z
  .object({
    id: z.string().openapi({ description: 'Script unique identifier', example: 'script_dialogue_456' }),
    workspaceId: z.string().openapi({ description: 'Owning workspace identifier', example: 'ws_01HXYZ' }),
    userId: z.string().nullable().optional().openapi({ description: 'User author identifier', example: 'usr_001' }),
    title: z.string().min(1).max(200).openapi({ description: 'Dialogue script title', example: 'Tech Debate: Edge Computing vs Cloud Monolith' }),
    description: z.string().max(1000).optional().openapi({ description: 'Script summary or context notes' }),
    speakers: z.array(ScriptSpeakerSchema).default([]).openapi({ description: 'List of participating voice actors/characters' }),
    blocks: z.array(ScriptBlockSchema).default([]).openapi({ description: 'Sequential list of speech blocks' }),
    totalDuration: z.number().min(0).default(0).openapi({ description: 'Total sequential duration including pauses in seconds', example: 145.8 }),
    estimatedCredits: z.number().min(0).default(0).openapi({ description: 'Estimated credit cost to synthesize entire script', example: 4.5 }),
    createdAt: z.string().openapi({ description: 'ISO creation timestamp', example: '2026-10-21T10:00:00.000Z' }),
    updatedAt: z.string().openapi({ description: 'ISO update timestamp', example: '2026-10-21T10:00:00.000Z' }),
  })
  .openapi('DialogueScript');

export type DialogueScriptDto = z.infer<typeof DialogueScriptSchema>;

/**
 * Payload for creating or updating a Dialogue Script.
 */
export const SaveDialogueScriptSchema = z
  .object({
    title: z.string().min(1).max(200).openapi({ description: 'Script title', example: 'Customer Support Roleplay' }),
    description: z.string().max(1000).optional().openapi({ description: 'Optional description' }),
    speakers: z.array(ScriptSpeakerSchema).default([]).openapi({ description: 'Participating character speakers' }),
    blocks: z.array(ScriptBlockSchema).default([]).openapi({ description: 'Dialogue sentence blocks' }),
  })
  .openapi('SaveDialogueScript');

export type SaveDialogueScriptDto = z.infer<typeof SaveDialogueScriptSchema>;

/**
 * Request payload for synthesizing an individual sentence block (Independent Segment Synthesis).
 */
export const SynthesizeScriptBlockRequestSchema = z
  .object({
    blockId: z.string().min(1).openapi({ description: 'Block identifier to synthesize or regenerate', example: 'blk_01HXYZ123' }),
    providerOverride: SpeakerVoiceProviderSchema.optional().openapi({ description: 'Optional temporary provider override' }),
    voiceIdOverride: z.string().optional().openapi({ description: 'Optional temporary voice identifier override' }),
  })
  .openapi('SynthesizeScriptBlockRequest');

export type SynthesizeScriptBlockRequestDto = z.infer<typeof SynthesizeScriptBlockRequestSchema>;

/**
 * Response payload for synthesized sentence block.
 */
export const SynthesizeScriptBlockResponseSchema = z
  .object({
    blockId: z.string().openapi({ description: 'Block identifier', example: 'blk_01HXYZ123' }),
    audioUrl: z.string().url().openapi({ description: 'Public URL of synthesized audio clip', example: 'https://media.sophiaplatform.com/audio/ws_01/dialogue/blk_1.mp3' }),
    r2Key: z.string().openapi({ description: 'Cloudflare R2 destination key', example: 'audio/ws_01/dialogue/blk_1.mp3' }),
    duration: z.number().min(0).openapi({ description: 'Accurate audio duration in seconds', example: 3.42 }),
    status: ScriptBlockStatusSchema,
    creditsDeducted: z.number().min(0).openapi({ description: 'Credits deducted for this sentence synthesis', example: 0.15 }),
    script: DialogueScriptSchema.openapi({ description: 'Updated script with recalculated timeline positions' }),
  })
  .openapi('SynthesizeScriptBlockResponse');

export type SynthesizeScriptBlockResponseDto = z.infer<typeof SynthesizeScriptBlockResponseSchema>;

/**
 * Request payload for recalculating Auto-Sequence Timeline.
 */
export const AutoSequenceTimelineRequestSchema = z
  .object({
    initialDelay: z.number().min(0).default(0).openapi({ description: 'Initial silence offset in seconds before first dialogue', example: 0.5 }),
    defaultPauseAfter: z.number().min(0).max(10).optional().openapi({ description: 'Optional default pause after sentences in seconds', example: 0.5 }),
  })
  .openapi('AutoSequenceTimelineRequest');

export type AutoSequenceTimelineRequestDto = z.infer<typeof AutoSequenceTimelineRequestSchema>;

/**
 * Response for dialogue to mixer project conversion.
 */
export const DialogueScriptToMixerResponseSchema = z
  .object({
    mixerProjectId: z.string().openapi({ description: 'Generated AudioMixerProject ID', example: 'mix_proj_98765' }),
    mixerProject: AudioMixerProjectSchema.openapi({ description: 'Complete multi-track mixer project ready for BGM & SFX' }),
  })
  .openapi('DialogueScriptToMixerResponse');

export type DialogueScriptToMixerResponseDto = z.infer<typeof DialogueScriptToMixerResponseSchema>;
